import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import {
  Group,
  Box3,
  Vector3,
  Object3D,
  Mesh,
  Material,
  BufferGeometry,
} from 'three';

export interface CharacterHandle {
  group: Group | null;
  dispose: () => void;
  loaded?: boolean;
  error?: string | null;
}

// Load the meditation pose GLB model and place it in the provided scene. This is
// intentionally inert — no animations or behavior are applied here.
export default function loadCharacter(scene: Group): CharacterHandle {
  let group: Group | null = null;
  const loader = new GLTFLoader();
  let cancelled = false;

  // Default placement values (chosen via debug panel).
  // The breath ring defaults are tuned to this same anchor so the model
  // and ring are aligned in screen space.
  const DEFAULT_ANCHOR = {
    x: 0.3,
    y: 0.35,
    z: -1.5,
    rotationYDeg: -20, // face camera by default
  };

  const ch: CharacterHandle = {
    group: null,
    loaded: false,
    error: null,
    dispose: () => {
      cancelled = true;
      if (!group) return;
      try {
        scene.remove(group);
      } catch (e) {
        void e;
      }
      try {
        group.traverse((o: Object3D) => {
          const mesh = o as Mesh;
          if (
            mesh.geometry &&
            typeof (mesh.geometry as BufferGeometry).dispose === 'function'
          ) {
            (mesh.geometry as BufferGeometry).dispose();
          }
          const mat = mesh.material as Material | Material[] | undefined;
          if (!mat) return;
          if (Array.isArray(mat)) {
            mat.forEach((m) => {
              if (m && typeof (m as Material).dispose === 'function') {
                (m as Material).dispose();
              }
            });
          } else if (typeof (mat as Material).dispose === 'function') {
            (mat as Material).dispose();
          }
        });
      } catch (e) {
        void e;
      }
      group = null;
      ch.loaded = false;
    },
  } as CharacterHandle;

  type MinimalGLTF = {
    scene?: Group;
    scenes?: Group[];
    asset?: { generator?: string };
    animations?: unknown[];
  };

  const onLoad = (gltf: MinimalGLTF) => {
    if (cancelled) return;
    const root = (gltf.scene || gltf.scenes?.[0]) as Group | undefined;
    if (!root) return;

    // Wrap the model in a parent group so we can center the model geometry
    // without losing the tuned world-space anchor (ring + aura are aligned
    // around DEFAULT_ANCHOR).
    const model = root.clone();
    const wrapper = new Group();
    wrapper.name = 'characterWrapper';
    wrapper.add(model);
    group = wrapper;

    // Center the model so its bounding-box center sits at the wrapper origin.
    // This makes the model appear centered within the breath ring.
    let computedScale: number | undefined;
    try {
      const box = new Box3().setFromObject(model as Object3D);
      const size = box.getSize(new Vector3());
      const center = box.getCenter(new Vector3());

      // Move model so its visual center is at wrapper origin.
      model.position.set(-center.x, -center.y, -center.z);

      // Scale to desired height - tuned for this scene's framing.
      const desiredHeight = 1.1; // world units
      if (size.y > 0) {
        computedScale = desiredHeight / size.y;
        wrapper.scale.setScalar(computedScale);
      }
    } catch (e) {
      // Non-fatal; if bounding box fails, we'll still place the wrapper.
      // eslint-disable-next-line no-console
      console.warn('loadCharacter: bounding box failed', e);
    }

    // Place the wrapper at the tuned anchor so it aligns with the ring.
    try {
      wrapper.position.set(
        DEFAULT_ANCHOR.x,
        DEFAULT_ANCHOR.y,
        DEFAULT_ANCHOR.z
      );
      if (typeof computedScale === 'undefined') {
        wrapper.scale.setScalar(0.01);
      }
      // If this model is the new meditation_pose_female asset, it may have
      // a different bind rotation. Apply an asset-specific override to make
      // the character face the camera by default.
      const modelUrl = (model.userData?.source as string | undefined) ?? '';
      const generator = (gltf.asset?.generator as string | undefined) ?? '';
      const isMeditationPose =
        modelUrl.includes('meditation_pose_female') ||
        generator.includes('meditation');
      if (isMeditationPose) {
        wrapper.rotation.y = 0; // face camera
      } else {
        wrapper.rotation.y = (DEFAULT_ANCHOR.rotationYDeg * Math.PI) / 180;
      }
    } catch (e) {
      void e;
    }

    // Add to scene
    scene.add(group);
    ch.group = group;
    // mark loaded for debug
    ch.loaded = true;
    // Helpful debug for visibility issues: log bounding box size and computed
    // scale so devs can see why a model may be tiny/large/offset.
    try {
      const box = new Box3().setFromObject(group as Object3D);
      const size = box.getSize(new Vector3());
      // eslint-disable-next-line no-console
      console.debug(
        'loadCharacter: bounding size',
        size,
        'computedScale',
        computedScale
      );
    } catch (e) {
      void e;
    }
    // Helpful debug so you can see loading succeeded in the browser console
    // (useful when troubleshooting why the model isn't visible).
    // eslint-disable-next-line no-console
    console.debug('loadCharacter: model loaded and added to scene');
  };

  // Try multiple GLB URLs in order — helpful when the new model isn't published
  // to Supabase yet or is named differently. This attempts the 'meditation'
  // model first, then falls back to the original open-source model.
  const modelUrls = [
    'https://hsfbmpaclvbltdfaazpo.supabase.co/storage/v1/object/public/buddha_game/meditation_pose_female.glb',
    'https://hsfbmpaclvbltdfaazpo.supabase.co/storage/v1/object/public/buddha_game/open_source_buddha.glb',
  ];

  let attemptIndex = 0;
  const tryLoad = () => {
    const url = modelUrls[attemptIndex];
    if (!url) {
      ch.error = 'no model url to load';
      return;
    }
    // eslint-disable-next-line no-console
    console.debug('loadCharacter: loading model URL', url);
    loader.load(url, onLoad, undefined, (err: unknown) => {
      // Loading failed — try next one or set an error
      // eslint-disable-next-line no-console
      console.warn('loadCharacter: failed to load model', url, err);
      attemptIndex += 1;
      if (attemptIndex < modelUrls.length) {
        tryLoad();
      } else {
        ch.error = String(err ?? 'unknown');
      }
    });
  };

  tryLoad();

  return ch;
}
